import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getDashboard } from "../../apis/Dashboard.js";
import { Link } from "react-router-dom";
import { Dropdown, Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import PerfectScrollbar from "react-perfect-scrollbar";
import Avatar from "../../components/Avatar";
import CustomToggle from "../../components/CustomToggle";
import { matchesGuruRouteName } from "../../utils/guruRoute";
import img14 from "../../assets/img/img14.jpg";

const TITLE_MAP = {
  guru: "글로벌 투자대가",
  fund: "미국연기금",
  algo: "투자 아이디어",
};

// 최근 업데이트 기준 시간
const RECENT_CUTOFF_MS = 90 * 24 * 60 * 60 * 1000;
const DASHBOARD_PAGE_SIZE = 8;
const RECENT_UPDATE_TOOLTIP = "미국증권감독위원회(SEC) 공시발표일 기준으로 표시합니다.";
const RANK_TOOLTIP = "Top10 종목 수익률 YoY(전년 대비) 성과를 기준으로 산정됩니다.";
const ALGO_AVATAR_BG_CLASSES = ["primary", "secondary", "success", "info", "danger", "dark"];

function toDashboardItems(data) {
  if (Array.isArray(data)) return data;
  return [];
}

// algo 카테고리 아바타 배경색을 seed 기반으로 일관되게 선택한다.
function getAlgoAvatarBg(seedValue) {
  const seed = String(seedValue || "");
  if (!seed) return ALGO_AVATAR_BG_CLASSES[0];
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) >>> 0;
  }
  return ALGO_AVATAR_BG_CLASSES[hash % ALGO_AVATAR_BG_CLASSES.length];
}

function SidebarAvatar({ dashboard, status = "", forceInitial = false }) {
  const initialChar = (dashboard?.leader || dashboard?.nickname || dashboard?.guruid || "?").charAt(0);
  const algoBg = useMemo(() => {
    return getAlgoAvatarBg(`${dashboard?.guruid || ""}-${dashboard?.leader || dashboard?.nickname || ""}`);
  }, [dashboard?.guruid, dashboard?.leader, dashboard?.nickname]);
  const defaultSrc = dashboard?.imagefile ? `/images/webp/${dashboard.guruid}.webp` : img14;
  const [imgSrc, setImgSrc] = useState(defaultSrc);

  useEffect(() => {
    setImgSrc(defaultSrc);
  }, [defaultSrc]);

  // algo는 이니셜 아바타를 강제하고, 일반 카테고리는 이미지 fallback 체인을 사용한다.
  if (forceInitial) {
    return (
      <Avatar
        initial={initialChar}
        bg={algoBg}
        shape="rounded"
        status={status}
      />
    );
  }

  if (imgSrc) {
    return (
      <div className={`avatar ${status}`}>
        <img
          src={imgSrc}
          alt=""
          onError={() => {
            if (imgSrc !== img14) {
              setImgSrc(img14);
              return;
            }
            setImgSrc(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className={`avatar ${status}`}>
      <span className="avatar-initial">{initialChar}</span>
    </div>
  );
}

export default function PortfolioSidebar({ category, onSelectDashboard, requestedGuruName = "" }) {
  const normalizedCategory = typeof category === "string" ? category : "";
  const isAlgoCategory = normalizedCategory.toUpperCase() === "ALGO";
  // 카테고리별 dashboard 목록은 react-query 캐시로 관리한다.
  const dashboardList = useQuery({
    queryKey: ["dashboard", normalizedCategory],
    queryFn: () => getDashboard(normalizedCategory),
    staleTime: 1000 * 60 * 30,
  });

  const dashboards = useMemo(() => toDashboardItems(dashboardList.data), [dashboardList.data]);
  const onSelect = onSelectDashboard;
  const [title, setTitle] = useState("");
  const [recentList, setRecentList] = useState([]);
  const [visibleCount, setVisibleCount] = useState(DASHBOARD_PAGE_SIZE);
  const [chatActive, setChatActive] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // 화면 크기가 991px 이하는 모바일 환경으로 판단
  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 991.98px)");
    const handleChange = () => setIsMobile(mediaQuery.matches);
    handleChange();
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // 사이드바 타이틀 세팅
  useEffect(() => {
    setTitle(TITLE_MAP[normalizedCategory] || "");
  }, [normalizedCategory]);

  useEffect(() => {
    setVisibleCount(DASHBOARD_PAGE_SIZE);
    setChatActive(null);
  }, [normalizedCategory]);

  // URL의 guruName과 실제 dashboard 항목을 매칭해 자동 선택한다.
  const getRequestedDashboardIndex = useCallback(() => {
    if (!requestedGuruName) return -1;
    return dashboards.findIndex((dashboard) => matchesGuruRouteName(dashboard, requestedGuruName));
  }, [dashboards, requestedGuruName]);

  // 최초 진입/카테고리 변경 시 선택 대상을 자동 확정한다.
  useEffect(() => {
    if (dashboards.length === 0) return;
    const requestedIndex = getRequestedDashboardIndex();
    if (requestedIndex >= 0) {
      if (chatActive === requestedIndex) return;
      setChatActive(requestedIndex);
      onSelectDashboard(dashboards[requestedIndex], { fromAuto: true });
      return;
    }

    if (chatActive !== null) return;
    setChatActive(0);
    onSelectDashboard(dashboards[0], { fromAuto: true });
  }, [dashboards, chatActive, onSelectDashboard, getRequestedDashboardIndex]);

  // "Recently Update" 목록은 최근 90일 최신 공시 기준 상위 6개만 노출한다.
  useEffect(() => {
    setRecentList(
      dashboards
        .filter((dashboard) => {
          if (!dashboard?.latest13f) return false;
          const updated = new Date(dashboard.latest13f).getTime();
          return Date.now() - updated <= RECENT_CUTOFF_MS;
        })
        .sort((a, b) => new Date(b.latest13f) - new Date(a.latest13f))
        .slice(0, 6)
    );
  }, [dashboards]);

  // 스크롤 페이징으로 보이는 사이드바 목록 수를 점진적으로 늘린다.
  const visibleDashboards = useMemo(() => {
    return dashboards.slice(0, visibleCount);
  }, [dashboards, visibleCount]);
  const hasMoreDashboards = visibleDashboards.length < dashboards.length;

  // 스크롤 하단 도달 시 다음 페이지 크기만큼 더 로드한다.
  const loadMoreDashboards = useCallback(() => {
    if (!hasMoreDashboards) return;
    setVisibleCount((prev) => Math.min(prev + DASHBOARD_PAGE_SIZE, dashboards.length));
  }, [hasMoreDashboards, dashboards.length]);

  const handleSelectDashboard = useCallback((guru, index) => {
    setChatActive(index);
    onSelect(guru, { fromAuto: false });
  }, [onSelect]);

  const sidebarStyle = isMobile
    ? { position: "relative", height: "calc(100vh)" }
    : undefined;

  return (
    <div className="chat-sidebar" style={sidebarStyle}>
      <div className="sidebar-header">
        <span className="sidebar-title me-auto">{title}</span>
        <Dropdown align="end">
          <Dropdown.Toggle as={CustomToggle}>
            <i className="ri-more-2-fill"></i>
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item href="/portfolio/guru">글로벌 투자대가</Dropdown.Item>
            <Dropdown.Item href="/portfolio/fund">미국연기금</Dropdown.Item>
            <Dropdown.Item href="/portfolio/algo">투자 아이디어</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <PerfectScrollbar className="sidebar-body" onYReachEnd={loadMoreDashboards}>
        <div className="d-flex align-items-center gap-1 mb-3">
          <label className="sidebar-label mb-0">Recently Update</label>
          <OverlayTrigger
            trigger={["hover", "focus", "click"]}
            placement="right"
            rootClose
            overlay={(
              <Tooltip style={{ fontWeight: "600" }}>
                {RECENT_UPDATE_TOOLTIP}
              </Tooltip>
            )}
          >
            <button
              type="button"
              className="btn btn-link p-0 text-secondary lh-1"
              aria-label="Recently Update 기준 설명"
            >
              <i className="ri-question-line"></i>
            </button>
          </OverlayTrigger>
        </div>
        <div className="chat-contacts mb-4">
          {recentList.length > 0 ? (
            <Row className="g-2 row-cols-auto">
              {recentList.map((dashboard, index) => (
                <Col key={`recent-${index}`}>
                  <Link
                    to=""
                    onClick={(e) => {
                      e.preventDefault();
                      const dashboardIndex = dashboards.findIndex((item) => item?.guruid === dashboard?.guruid);
                      handleSelectDashboard(dashboard, dashboardIndex >= 0 ? dashboardIndex : 0);
                    }}
                  >
                    <SidebarAvatar dashboard={dashboard} status="online" forceInitial={isAlgoCategory} />
                  </Link>
                </Col>
              ))}
            </Row>
          ) : (
            <div className="text-secondary fs-sm py-2 px-1">
              최근 한 달 이내 소식이 없습니다.
            </div>
          )}
        </div>

        <div className="d-flex align-items-center gap-1 mb-2">
          <label className="sidebar-label mb-0">글로벌투자대가 순위</label>
          <OverlayTrigger
            trigger={["hover", "focus", "click"]}
            placement="right"
            rootClose
            overlay={(
              <Tooltip style={{ fontWeight: "700" }} id={`rank-tooltip-${normalizedCategory || "all"}`}>
                {RANK_TOOLTIP}
              </Tooltip>
            )}
          >
            <button
              type="button"
              className="btn btn-link p-0 text-secondary lh-1"
              aria-label="순위 기준 설명"
            >
              <i className="ri-question-line"></i>
            </button>
          </OverlayTrigger>
        </div>
        <div className="chat-group">
          {dashboardList.isLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : dashboardList.isError ? (
            <div className="text-center py-4 text-secondary">
              데이터를 불러오지 못했습니다.
            </div>
          ) : visibleDashboards.length > 0 ? (
            <>
              {visibleDashboards.map((dashboard, index) => (
                <div
                  key={`${dashboard.guruid}-${index}`}
                  className={"chat-item" + (chatActive === index ? " selected" : "")}
                  onClick={() => handleSelectDashboard(dashboard, index)}
                >
                  <SidebarAvatar dashboard={dashboard} status="online" forceInitial={isAlgoCategory} />
                  <div className="chat-item-body">
                    <div className="d-flex align-items-center mb-1">
                      <span className="chat-item-title">{dashboard.leader}</span>
                    </div>
                    <span>{dashboard.institution || ""}</span>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="text-center py-4 text-secondary">
              데이터가 없습니다
            </div>
          )}
        </div>
      </PerfectScrollbar>
    </div>
  );
}
